import React from 'react';

import { Box, Button, radioClasses, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { Field, Form, FormSpy } from '@hrlogics/react-components/form';
import { SelectFieldOption } from '@hrlogics/react-helper-types';

import {
  appRoutes,
  EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES,
  RouterEntitiesId,
} from '@clearverify/constants';

import { PeoCompanyType, SignUpMethod } from 'src/api';
import { RadioGroupField, SelectField, TextField } from 'src/components/finalForm';
import { SIGN_UP_METHOD_OPTIONS } from 'src/constants/employer';
import { usePeoById, usePeoParamId } from 'src/hooks/peo';
import { useCurrentUserPermissions } from 'src/hooks/user';
import type { SxProp } from 'src/types/mui';
import { buildUrl } from 'src/utils/router';

import { PeoLiveStatusSettings } from './components';
import { PeoSettingsFormFields, PeoSettingsFormValues } from './types';
import { peoSettingsFormValidationSchema } from './utils';

export const PeoSettingsForm: React.FC = () => {
  const navigate = useNavigate();

  const peoId = usePeoParamId();
  const { peo, updatePeo } = usePeoById(peoId);

  const { isPlatformAdmin } = useCurrentUserPermissions();

  const initialValues = React.useMemo(
    (): Partial<PeoSettingsFormValues> => ({
      [PeoSettingsFormFields.companyType]: peo?.companyType,
      [PeoSettingsFormFields.signUpMethod]: peo?.signUpMethod,
      [PeoSettingsFormFields.pinHint]: peo?.pinHint,
    }),
    [peo?.companyType, peo?.signUpMethod, peo?.pinHint],
  );

  const onSubmit = React.useCallback(
    async (formData: PeoSettingsFormValues) => {
      try {
        await updatePeo({
          companyType: formData.companyType,
          signUpMethod: formData.signUpMethod,
          pinHint: formData.signUpMethod === SignUpMethod.Pin ? (formData.pinHint ?? null) : null,
          ...EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES,
        });

        if (formData.companyType !== initialValues.companyType) {
          const urlPattern =
            formData.companyType === PeoCompanyType.Peo
              ? appRoutes.peos.detail.settings
              : appRoutes.partnerships.detail.peo.settings;
          navigate(buildUrl(urlPattern, { [RouterEntitiesId.peo]: peoId }));
        }
      } catch (error) {
        console.error(error);
      }
    },
    [peoId, initialValues.companyType, navigate, updatePeo],
  );

  return (
    <Form<PeoSettingsFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ dirty: true, submitting: true, valid: true }}
      validationSchema={peoSettingsFormValidationSchema}
    >
      {({ form, dirty, submitting, valid }) => (
        <Box sx={boxStyles} rowGap={3}>
          <React.Activity mode={isPlatformAdmin ? 'visible' : 'hidden'}>
            <Box sx={settingsStyles}>
              <Typography variant="subtitle1" fontSize={16}>
                Organization Type
              </Typography>

              <Typography variant="body1" color="text.secondary" mt={1}>
                Specify whether this entity is a PEO, or a Partnership. Unlike PEO users,
                Partnership entity users do not own the data and are not able to view employment
                data and verifications.
              </Typography>

              <Box mt={2}>
                <Field
                  label="Organization Type"
                  name={PeoSettingsFormFields.companyType}
                  fullWidth
                  disabled={submitting}
                  component={SelectField}
                  options={companyTypeSelectFieldValues}
                />
              </Box>
            </Box>

            <PeoLiveStatusSettings />
          </React.Activity>

          <Box sx={settingsStyles}>
            <Typography variant="subtitle1" fontSize={16}>
              Employee Sign Up PIN
            </Typography>
            <Typography variant="body1" color="text.secondary" mt={1}>
              Employees will use this 8 digit PIN as the first part of the sign-up process to
              confirm their identity. <br /> This is an optional additional layer of security.
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Format:
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={titleDataSchemaStyles}>
              XXXX-YYYY where XXXX - 4 last digits of SSN, YYYY - year of birth
            </Typography>

            <Field
              fullWidth
              name={PeoSettingsFormFields.signUpMethod}
              component={RadioGroupField}
              options={SIGN_UP_METHOD_OPTIONS}
              disabled={submitting}
              sx={radioFieldStyles}
            />

            <FormSpy<PeoSettingsFormValues> subscription={{ values: true }}>
              {({ values }) => {
                if (values.signUpMethod === SignUpMethod.Pin) {
                  return (
                    <Box>
                      <Typography variant="body1" color="text.secondary" mt={1} mb={2}>
                        Enter your PIN hint for employees here (optional, 200 characters max)
                      </Typography>

                      <Field
                        name={PeoSettingsFormFields.pinHint}
                        label="PIN Hint"
                        component={TextField}
                        disabled={submitting}
                        fullWidth
                      />
                    </Box>
                  );
                }
                return null;
              }}
            </FormSpy>
          </Box>

          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <Button
              color="error"
              disabled={!dirty}
              size="medium"
              type="reset"
              variant="outlined"
              onClick={() => form.restart()}
            >
              Reset
            </Button>
            <Button
              disabled={!dirty || !valid}
              loading={submitting}
              variant="contained"
              type="submit"
            >
              Save changes
            </Button>
          </Box>
        </Box>
      )}
    </Form>
  );
};

const companyTypeSelectFieldValues: SelectFieldOption<PeoCompanyType>[] = [
  { label: 'PEO', value: PeoCompanyType.Peo },
  { label: 'Partnership', value: PeoCompanyType.Partnership },
];

const radioFieldStyles: SxProp = {
  marginLeft: 1,
  [`& .${radioClasses.checked}`]: {
    borderColor: 'red',
  },
  [`& .${radioClasses.root}`]: {
    fontSize: '1rem',
  },
};

const boxStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
};

const settingsStyles: SxProp = theme => ({
  border: theme.constantStyles?.paperBorder,
  borderRadius: theme.constantStyles?.paperBorderRadius,
  padding: '20px 24px',
});

const titleDataSchemaStyles: SxProp = {
  borderBottom: '1px solid #D9D9D9',
  paddingBottom: '16px',
  marginBottom: '16px',
};
